<template>
  <div id="actionPlanSurvey">
    <el-page-header
      :title="$t('common.goBack')"
      :content="null"
      class="mainCard"
      @back="$router.push({name: 'actionPlan'}).catch((err) => {})"
    />
    <el-card
      v-if="actionPlans"
      shadow="hover"
      class="mainCard clear-card surveyCard"
      :body-style="{padding: cardPadding}"
    >
      <div class="title">
        <div class="APNumber">
          {{ $t('actionPlan.actionPlanSurvey') }}
        </div>
        <div class="date">
          {{
            LeaderTimeZone ?
              $moment().tz(LeaderTimeZone).format('MMM DD, YYYY') :
              $moment().format('MMM DD, YYYY')
          }}
        </div>
      </div>

      <div
        v-for="(lga, lKey) in ['lga1', 'lga2']"
        :key="lKey"
        class="lgaWrap"
      >
        <div
          v-if="actionPlans[lga]"
          class="lga"
        >
          <el-table
            :data="actionPlans[lga].action_plan"
            style="width: 100%;"
            class="clear-table"
          >
            <el-table-column
              :label="actionPlans[lga].description"
              class-name="descriptionColumn"
              label-class-name="LGA"
            >
              <template slot-scope="scope">
                <div :class="{disabled: scope.row.is_disabled}">
                  {{ scope.row.description }}
                  <span class="period">
                    ({{ scope.row.period }})&nbsp;
                    <el-tooltip
                      v-if="scope.row.period !== 'daily'"
                      effect="dark"
                      placement="right-start"
                    >
                      <div
                        slot="content"
                      >
                        <div v-if="scope.row.is_disabled">
                          <i18n path="actionPlan.answered">
                            <template #period>
                              {{ scope.row.period === 'weekly' ? 'week' : 'month' }}
                            </template>
                          </i18n>
                        </div>
                        <div v-else>
                          <i18n path="actionPlan.answerOption">
                            <template #info>
                              {{ scope.row.period }}
                            </template>
                            <template #period>
                              {{ scope.row.period === 'weekly' ? 'week' : 'month' }}
                            </template>
                          </i18n>
                        </div>
                      </div>
                      <i class="el-icon-info"></i>
                    </el-tooltip></span>
                </div>
              </template>
            </el-table-column>
            <el-table-column
              prop="name"
              width="100px"
              class-name="responseColumn"
            >
              <div
                slot="header"
                class="layout"
              >
                <div class="left title">
                  {{ $t('common.yes') }}
                </div>
                <div class="right title">
                  {{ $t('common.no') }}
                </div>
              </div>

              <template slot-scope="scope">
                <el-radio-group
                  v-if="scope.row.period === 'daily'"
                  v-model="resp[lga][scope.row.id]"
                  :disabled="scope.row.is_disabled"
                  class="answer"
                >
                  <div class="layout">
                    <div class="left">
                      <el-radio :label="true">
                      &nbsp;
                      </el-radio>
                    </div>
                    <div class="right">
                      <el-radio :label="false">
                      &nbsp;
                      </el-radio>
                    </div>
                  </div>
                </el-radio-group>

                <div
                  v-else
                  class="answer"
                >
                  <div class="layout">
                    <div class="left">
                      <el-checkbox
                        v-model="resp[lga][scope.row.id]"
                        :disabled="scope.row.is_disabled"
                      >
                      &nbsp;
                      </el-checkbox>
                    </div>
                    <div class="right">
                      &nbsp;
                    </div>
                  </div>
                </div>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </el-card>

    <div
      v-if="actionPlans"
      class="actions mainCard"
    >
      <el-button
        type="primary"
        round
        :disabled="disableSubmit"
        @click="submitSurvey"
      >
        {{ $t('common.submit') }}
      </el-button>
    </div>

    <ExtendDialog
      :extension-notice="extension_notice"
      :mode="'extendInner'"
      :show.sync="extendDialog"
    />

    <ApPauseDialog
      :show.sync="apPauseDialog"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex';
import api from '@/api';
import ExtendDialog from '@/views/dashboard/layout/customAlerts/component/ExtendDialog.vue';
import ApPauseDialog from './ApPauseDialog.vue';

export default {
  components: {
    ExtendDialog,
    ApPauseDialog,
  },

  data() {
    return {
      extendDialog: false,
      apPauseDialog: false,
      actionPlans: null,
      resp: {
        lga1: [],
        lga2: [],
      },
    };
  },

  computed: {
    ...mapState({
      LeaderTimeZone: (state) => state.user.user.time_zone,
      extension_notice: (state) => state.processStatus.process.extension_notice,
    }),

    formattedResponses() {
      const data = {};
      const that = this;

      ['lga1', 'lga2'].forEach((lga) => {
        if (Object.keys(this.resp[lga]).length > 0) {
          Object.entries(this.resp[lga]).forEach((el) => {
            if (!data[lga]) data[lga] = [];

            if (el[1] === false) {
              // check if not daily
              const actionPlan = that.actionPlans[lga].action_plan.find(
                (ap) => ap.id === parseInt(el[0], 10),
              );

              // if answer is false and period is daily, we add to answer array
              if (actionPlan.period === 'daily') {
                data[lga].push({
                  id: el[0],
                  response: el[1],
                });
              }
            } else {
              data[lga].push({
                id: el[0],
                response: el[1],
              });
            }
          });
        }
      });

      return data;
    },

    disableSubmit() {
      if (this.formattedResponses.lga1 && this.formattedResponses.lga1.length > 0) {
        return false;
      }
      if (this.formattedResponses.lga2 && this.formattedResponses.lga2.length > 0) {
        return false;
      }
      return true;
    },
    cardPadding() {
      let padding = '';
      if (this.$mq === 'tablet' || this.$mq === 'mobile') {
        padding = '20px 20px';
      } else {
        padding = '20px 160px';
      }
      return padding;
    },
  },

  created() {
    this.getActionPlans();
  },

  methods: {
    async getActionPlans() {
      try {
        const response = await api.getActionPlanSubmit();
        this.actionPlans = response.data;
        this.sortActionPlan();

        this.resp.lga1 = {};
        this.resp.lga2 = {};
      } catch (err) {
        if (err.msg) {
          this.$message.error(err.msg);
          this.$router.push({ name: 'actionPlan' }).catch((err2) => {});
        }
      }
    },

    sortActionPlan() {
      let dailyAP = [];
      let weeklyAP = [];
      let monthlyAP = [];

      ['lga1', 'lga2'].forEach((lga) => {
        dailyAP = this.actionPlans[lga].action_plan.filter((ap) => ap.period === 'daily');
        weeklyAP = this.actionPlans[lga].action_plan.filter((ap) => ap.period === 'weekly');
        monthlyAP = this.actionPlans[lga].action_plan.filter((ap) => ap.period === 'monthly');

        this.actionPlans[lga].action_plan = [...dailyAP, ...weeklyAP, ...monthlyAP];
      });
    },

    async submitSurvey() {
      try {
        await api.submitActionPlanSurvey(this.formattedResponses);
        const that = this;

        this.$alert(this.$t('actionPlan.surveySubmitted'),
          this.$t('actionPlan.wellDone'), {
            confirmButtonText: this.$t('common.close'),
            roundButton: true,
            customClass: 'actionPlanSurveySubmitConfirmation',
            callback: (action) => {
              if (action === 'confirm') {
                that.$router.push({ name: 'actionPlan' }).catch((err) => {});
              }
            },
          });
      } catch (err) {
        if (err.key && err.key === 'shouldExtendEngagement'
          && Object.entries(this.extension_notice).length > 0) {
          this.extendDialog = true;
        } else if (err.key && err.key === 'shouldExtendEngagement'
          && Object.entries(this.extension_notice).length === 0) {
          this.apPauseDialog = true;
        } else if (err.msg) {
          this.$message.error(err.msg);
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  #actionPlanSurvey {
    .el-page-header {
      margin-bottom: 20px;
    }

    .surveyCard {
      margin-bottom: 20px;

      .title {
        display: flex;
        justify-content: space-between;
        padding-left: 10px;
        margin-bottom: 20px;
      }

      .lgaWrap {
        margin-bottom: 60px;

        &:last-child {
          margin-bottom: 0;
        }

        .lga {
          .el-table {
            background-color: transparent;
            border-radius: 0;

            &:hover {
              box-shadow: unset;
            }

            .descriptionColumn {
              .cell {
                padding-right: 25px;

                &.LGA {
                  font-size: 17px;
                }

                .disabled {
                  color: #c4c4c4;
                }

                .period {
                  font-style: italic;
                }
              }
            }

            .responseColumn {
              .cell {
                padding: 0;

                .left {
                  &.title {
                    padding: 0 10px 0 2px;
                  }
                }

                .right {
                  &.title {
                    padding: 0 5px 0 0;
                  }
                }

                .answer {
                  width: 100%;
                  padding: 0 5px;
                }

                .layout {
                  padding: 0;
                  width: 100%;
                  display: flex;
                  justify-content: space-between;
                }
              }
            }
          }
        }
      }
    }

    .actions {
      text-align: center;
    }
  }
</style>

<style lang="scss">
  #actionPlanSurvey {
    .surveyCard {
      .el-card__body {
        padding: 40px 10% 50px !important;
      }

      .el-table {
        tr:hover > td {
          background-color: transparent !important;
        }

        .el-table__header-wrapper {
          th {
            border-bottom: 0;
          }
        }

        .el-table__body-wrapper {
          .el-table__row {
            height: 55px;
          }
        }

        .answer {
          &.el-radio-group {
            .el-radio__label {
              display: none;
            }

            .el-radio__input {
              .el-radio__inner {
                border-color: var(--themeFontColor);
                background-color: transparent;
                width: 20px;
                height: 20px;
              }

              &.is-checked {
                .el-radio__inner {
                  background-color: white;

                  &::after {
                    width: 8px;
                    height: 8px;
                    background-color: #003865;
                  }
                }
              }

              &.is-disabled {
                .el-radio__inner {
                  border-color: #dcdfe6;
                  background-color: rgba(255, 255, 255, 0.2);
                }
              }
            }
          }

          .el-checkbox {
            .el-checkbox__label {
              display: none;
            }

            .el-checkbox__input {
              .el-checkbox__inner {
                border-color: var(--themeFontColor);
                border-radius: 50%;
                background-color: transparent;
                width: 20px;
                height: 20px;
              }

              &.is-checked {
                .el-checkbox__inner {
                  background-color: white;

                  &::after {
                    border: 3px solid #003865;
                    border-left: 0;
                    border-top: 0;
                    height: 15px;
                    width: 6px;
                    top: -2px;
                  }
                }
              }

              &.is-disabled {
                .el-checkbox__inner {
                  border-color: #dcdfe6;
                  background-color: rgba(255, 255, 255, 0.2);
                }
              }
            }
          }
        }
      }
    }
  }

  .actionPlanSurveySubmitConfirmation {
    .el-message-box__btns {
      text-align: center;

      .el-button {
        width: 200px !important;
      }
    }
  }
</style>
